import { useMutation } from '@tanstack/react-query';
import { downloadFile, HttpMethod, FileDownloadResponse } from '@api/common';
import { showApiError } from '@api/queryClient';

// ============================================================================
// Types
// ============================================================================

export interface TankReportParams {
  tankId?: string;
  from?: string;
  to?: string;
  groupByReference?: boolean;
  groupByLoading?: boolean;
  includeInvoicingInfo?: boolean;
}

export interface CustomerReportParams {
  customerId?: string;
  terminalId?: string;
  from?: string;
  to?: string;
  groupByReference?: boolean;
  groupByLoading?: boolean;
  includeInvoicingInfo?: boolean;
}

export interface BookkeepingEntriesReportParams {
  terminalId?: string;
  from?: string;
  to?: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Trigger file download in browser
 */
const triggerFileDownload = (fileResponse: FileDownloadResponse): void => {
  const fileBlob =
    fileResponse.data instanceof Blob
      ? fileResponse.data
      : new Blob([fileResponse.data as ArrayBuffer], {
          type: fileResponse.contentType || 'application/octet-stream',
        });

  const url = window.URL.createObjectURL(fileBlob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileResponse.filename || 'report.xlsx';
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

// ============================================================================
// API Functions
// ============================================================================

const downloadTankReport = async (params: TankReportParams): Promise<FileDownloadResponse> => {
  const url = `Reports/tankReport/${params.tankId}?from=${params.from || ''}&to=${params.to || ''}&groupByReference=${params.groupByReference}&groupByLoadings=${params.groupByLoading}&includeInvoicingInfo=${params.includeInvoicingInfo}`;
  return downloadFile(HttpMethod.GET, url);
};

const downloadCustomerReport = async (params: CustomerReportParams): Promise<FileDownloadResponse> => {
  const url = `Reports/customerReport/${params.customerId}?terminalId=${params.terminalId}&from=${params.from || ''}&to=${params.to || ''}&groupByReference=${params.groupByReference}&groupByLoadings=${params.groupByLoading}&includeInvoicingInfo=${params.includeInvoicingInfo}`;
  return downloadFile(HttpMethod.GET, url);
};

const downloadBookkeepingEntriesReport = async (
  params: BookkeepingEntriesReportParams
): Promise<FileDownloadResponse> => {
  const terminalId = params.terminalId || '00000000-0000-0000-0000-000000000000';
  const url = `Reports/bookkeepingEntryReport/${terminalId}?from=${params.from || ''}&to=${params.to || ''}`;
  return downloadFile(HttpMethod.GET, url);
};

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Download tank report
 */
export const useDownloadTankReportMutation = () => {
  return useMutation({
    mutationFn: downloadTankReport,
    onSuccess: (fileResponse) => {
      triggerFileDownload(fileResponse);
    },
    onError: (error: unknown) => {
      showApiError(error, 'reports.download_failed');
    },
  });
};

/**
 * Download customer report
 */
export const useDownloadCustomerReportMutation = () => {
  return useMutation({
    mutationFn: downloadCustomerReport,
    onSuccess: (fileResponse) => {
      triggerFileDownload(fileResponse);
    },
    onError: (error: unknown) => {
      showApiError(error, 'reports.download_failed');
    },
  });
};

/**
 * Download bookkeeping entries report
 */
export const useDownloadBookkeepingEntriesReportMutation = () => {
  return useMutation({
    mutationFn: downloadBookkeepingEntriesReport,
    onSuccess: (fileResponse) => {
      triggerFileDownload(fileResponse);
    },
    onError: (error: unknown) => {
      showApiError(error, 'reports.download_failed');
    },
  });
};
